import get from 'lodash.get';
import isNil from 'lodash.isnil';
import cloneDeep from 'lodash.clonedeep';
import { getCommonInterpretation } from '../../constants/shared/CommonInterpretationMapper';
import { mergeStudentData as sharedMergeStudentData } from '../shared/resultReducerHelper';
import { KEY } from '../../constants/shared/CommonConstants';
import { MEASURES_KEYS } from '../../constants/reading/MeasuresConstants';
import { BASIC_COMPREHENSION_MAX_SCORES_MAP_FOR_GRADES } from '../../constants/shared/BasicComprehensionConstants';
import { ON_GRADE_TEXT } from '../../constants/reading/TroConstants';
import { isBurstMeasure } from './filterUtils';
import { resultStateProperties } from '../../constants/reading/CommonConstants';
import {
    parseBurstResult,
    parseBurstStatus,
    parseTroMeasureResult,
    parseTroResult,
    parseTroStatus,
} from '../../api/results/reading/resultReducerParsers';

const { INTERPRETATION } = KEY;

const hasMeasureScores = (measure, scores) => scores && Object.keys(scores).includes(measure);
/**
 * Reformat from snake_case to camelCase, sort measures in the right order
 */
export const reformatMeasureScores = item => {
    const measureScores = {};

    if (!isNil(item.score)) {
        return {
            [item.measure]: {
                score: item.score,
                interp: getCommonInterpretation(item[INTERPRETATION]),
                maxScore: BASIC_COMPREHENSION_MAX_SCORES_MAP_FOR_GRADES[item.grade_level],
            },
        };
    }
    MEASURES_KEYS.forEach(
        measure =>
            hasMeasureScores(measure, item.measure_scores) &&
            Object.assign(measureScores, {
                [measure]: {
                    score: item.measure_scores[measure].score,
                    interp: getCommonInterpretation(item.measure_scores[measure][INTERPRETATION]),
                    maxScore: item.measure_scores[measure].max_score,
                },
            }),
    );
    return measureScores;
};

export const getBookResults = measureResult => get(measureResult, 'bookResults', []);

export const getCompletedBookResults = measureResult =>
    getBookResults(measureResult).filter(
        bookResult => bookResult.passingLevel || get(bookResult, 'vrrResult.isUntranscribable'),
    );

export const getBookById = (books, bookId) => books.find(book => book.bookId === bookId);

/**
 * * Check if there is an untranscribable book result
 */
export const hasUntranscribableResult = bookResults =>
    bookResults.some(
        bookResult =>
            get(bookResult, 'vrrResult.isUntranscribable') && get(bookResult, 'isValid', false),
    );

/**
 * * Find the On-Grade (Benchmark) TRO book result and return Voice Recording accuracy and wcpm (fluency)
 */
export const getOnGradeBookVrr = bookResults => {
    const onGradeBookResult = bookResults.find(
        bookResult => bookResult.relativeLevel === ON_GRADE_TEXT,
    );
    const { accuracy, score: wcpm } = get(onGradeBookResult, 'vrrResult', {});
    return { accuracy, wcpm };
};

export const getTroResult = result => {
    const measureResult = parseTroMeasureResult(get(result, 'measure_result'));
    return {
        result: parseTroResult(result, measureResult),
        status: parseTroStatus(result, measureResult),
    };
};

export const updateTroResultsStatuses = (
    data,
    { studentId, resultId, bookId, troAudioSrc, parsedResult },
) => {
    const currentStudent = cloneDeep(data[studentId]);
    const studentResults = get(currentStudent, 'results', []);
    const studentStatuses = get(currentStudent, 'statuses', []);

    if (parsedResult) {
        studentResults[studentResults.findIndex(oldResult => oldResult.resultId === resultId)] =
            parsedResult.result;

        studentStatuses[studentStatuses.findIndex(oldStatus => oldStatus.resultId === resultId)] =
            parsedResult.status;
    }
    const currentResult = studentResults.find(studentResult => studentResult.resultId === resultId);
    const currentBook = getBookById(getBookResults(currentResult.measureResult), bookId);

    currentBook.vrrResult = !isNil(troAudioSrc)
        ? { ...currentBook.vrrResult, audioSrc: troAudioSrc }
        : currentBook.vrrResult;

    return {
        ...data,
        [studentId]: { ...currentStudent, results: studentResults, statuses: studentStatuses },
    };
};

const updateStudentWithBurstData = (student, data, property, year) => {
    const studentData = get(student, property, []).filter(
        ({ measure, schoolYear }) => !isBurstMeasure(measure) || schoolYear !== year,
    );
    return { [property]: studentData.concat(data) };
};

export const updateBurstResultsForYear = (data, response) => {
    const { studentId, resultGroups, year } = get(response, 'JOULE', {});
    const statuses = resultGroups.map(burstResult => parseBurstStatus(burstResult));
    const results = resultGroups.map(burstResult => parseBurstResult(burstResult, studentId));

    const currentStudent = cloneDeep(data[studentId] || {});
    return {
        ...data,
        [studentId]: {
            ...currentStudent,
            ...updateStudentWithBurstData(currentStudent, statuses, 'statuses', year),
            ...updateStudentWithBurstData(currentStudent, results, 'results', year),
            fetchedYears: get(currentStudent, 'fetchedYears', []).concat(year),
        },
    };
};

export const mergeStudentData = (data, studentResultsMap) =>
    sharedMergeStudentData(data, studentResultsMap, resultStateProperties);
